import React, { createContext, useContext, useRef, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  Menu,
  MenuItem,
  SxProps,
  TextField,
} from "@mui/material";
import { MARKER_COLORS, MarkerColor } from "../../constants/styles/color";
import {
  COLOR_CONSTANTS,
  SPACING_CONSTANTS,
  FONT_SIZES,
} from "../../constants/styles";
import { useIsShown } from "../../../shared/hooks/useIsShown";
import { useOnClickOutside } from "../../../shared/hooks/useOnClickOutside";

// Constants
const OPACITY_OPTIONS = [
  { value: 0.1, label: "10%" },
  { value: 0.2, label: "20%" },
  { value: 0.3, label: "30%" },
  { value: 0.5, label: "50%" },
  { value: 0.7, label: "70%" },
  { value: 0.8, label: "80%" },
  { value: 0.9, label: "90%" },
  { value: 1.0, label: "100%" },
] as const;

const DROPDOWN_STYLES = {
  display: "flex",
  position: "absolute",
  justifyContent: "space-between",
  alignItems: "center",
  alignSelf: "stretch",
  padding: `${SPACING_CONSTANTS.spacing[2]}px`,
  top: "100%",
  left: 0,
  right: 0,
  zIndex: 1000,
  backgroundColor: COLOR_CONSTANTS.common.white_states.main,
  border: `1px solid ${COLOR_CONSTANTS.COLOR.GREY[300]}`,
  borderRadius: SPACING_CONSTANTS.borderRadius.sm,
  boxShadow:
    "0 1px 18px 0 rgba(0, 0, 0, 0.12), 0 6px 10px 0 rgba(0, 0, 0, 0.14), 0 3px 5px -1px rgba(0, 0, 0, 0.20)",
  marginTop: `${SPACING_CONSTANTS.spacing[1]}px`,
} as const;

const BUTTON_STYLES = {
  display: "flex",
  alignItems: "center",
  gap: `${SPACING_CONSTANTS.spacing[1]}px`,
  width: "100%",
  height: "30px",
  backgroundColor: COLOR_CONSTANTS.COLOR.GREY[200],
  color: COLOR_CONSTANTS.text.primary,
  fontSize: FONT_SIZES.sm,
  fontWeight: 500,
  fontFamily: "Pretendard",
  borderRadius: SPACING_CONSTANTS.borderRadius.sm,
  textTransform: "none",
  justifyContent: "space-between",
  padding: `0 ${SPACING_CONSTANTS.spacing[2]}px`,
  "&:hover": {
    backgroundColor: COLOR_CONSTANTS.COLOR.GREY[300],
  },
} as const;

interface ColorSelectContextProps {
  color: MarkerColor | undefined;
  opacity: number;
  changeColor: (color: MarkerColor) => void;
  changeOpacity: (opacity: number) => void;
}

const ColorSelectContext = createContext<ColorSelectContextProps>({
  color: "blue",
  opacity: 100,
  changeColor: () => {},
  changeOpacity: () => {},
});

export const useColorSelectContext = () => {
  return useContext(ColorSelectContext) as ColorSelectContextProps;
};

interface ColorSelectHeaderProps {
  color: MarkerColor | undefined;
  opacity: number;
  onClick: () => void;
}

interface ColorSelectContainerProps {
  children: React.ReactNode;
  onMenuStateChange: (isOpen: boolean) => void;
}

interface ColorSelectItemsProps {
  children: React.ReactNode;
}

interface ColorSelectItemProps {
  color: MarkerColor;
  isSelected: boolean;
  onClick: (color: MarkerColor) => void;
}

interface ColorOpacitySelectProps {
  opacity: number | undefined;
  onChange: (opacity: number) => void;
  onMenuStateChange: (isOpen: boolean) => void;
}

interface ColorBoxProps {
  color?: MarkerColor;
  size?: "small" | "medium";
  isSelected?: boolean;
  sx?: SxProps;
}

interface ColorSelectValue {
  color: MarkerColor;
  opacity: number;
}

/**
 * 색상 + 불투명도 동시 선택 컴포넌트의 props.
 * 불투명도 선택이 필요 없으면 {@link ColorPicker} 를 사용한다.
 *
 * @example
 * ```tsx
 * <ColorSelect
 *   colors={MARKER_COLORS}
 *   value={{ color: "blue", opacity: 0.8 }}
 *   onChange={(color, opacity) => handleColorChange(color, opacity)}
 * />
 * ```
 */
interface ColorSelectProps {
  /** 선택 가능한 색상 목록 */
  colors: MarkerColor[];
  /** 현재 선택된 색상·불투명도 */
  value: ColorSelectValue;
  /** 색상이나 불투명도가 변경될 때 호출 */
  onChange: (
    color: (typeof MARKER_COLORS)[number] | undefined,
    opacity: number | undefined
  ) => void;
}

function ColorBox({
  color = "blue",
  size = "small",
  isSelected = false,
  sx,
}: ColorBoxProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: size === "small" ? "20px" : "28px",
        height: size === "small" ? "20px" : "28px",
        borderRadius: SPACING_CONSTANTS.borderRadius.sm,
        ...(size === "small" && {
          backgroundColor: color,
        }),
        ...(size === "medium" && {
          padding: "1.8px",
        }),
        ...(isSelected && {
          border: `2px solid ${COLOR_CONSTANTS.text.primary}`,
        }),
        ...sx,
      }}
    >
      {size === "medium" && (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: color,
            borderRadius: SPACING_CONSTANTS.borderRadius.xs,
          }}
        />
      )}
    </Box>
  );
}

function ColorOpacitySelect({
  opacity,
  onChange,
  onMenuStateChange,
}: ColorOpacitySelectProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onMenuStateChange(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onMenuStateChange(false);
  };

  const handleSelect = (value: number) => {
    onChange(value);
    handleClose();
  };

  const currentOpacity =
    OPACITY_OPTIONS.find((opt) => opt.value === opacity) || OPACITY_OPTIONS[7];

  return (
    <Box>
      <Button onClick={handleClick} sx={BUTTON_STYLES}>
        <Box
          sx={{
            width: "12px",
            height: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "4px",
          }}
        >
          <svg
            width="12"
            height="15"
            viewBox="0 0 12 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 2.06433L2.70017 5.36416C0.87772 7.18663 0.87772 10.1414 2.70017 11.9638C4.52261 13.7863 7.4774 13.7863 9.2998 11.9638C11.1223 10.1414 11.1223 7.18663 9.2998 5.36416L6 2.06433ZM6 0.178711L10.2427 4.42135C12.5858 6.7645 12.5858 10.5635 10.2427 12.9066C7.89947 15.2498 4.10051 15.2498 1.75736 12.9066C-0.585787 10.5635 -0.585787 6.7645 1.75736 4.42135L6 0.178711ZM2.66667 8.66396H9.33333C9.33333 10.505 7.84093 11.9973 6 11.9973C4.15905 11.9973 2.66667 10.505 2.66667 8.66396Z"
              fill="#8F8F8F"
            />
          </svg>
        </Box>
        {currentOpacity.label}
        <Box
          sx={{
            width: "10px",
            height: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "5px",
          }}
        >
          <Box
            sx={{
              width: "2px",
              height: "2px",
              borderLeft: "3px solid transparent",
              borderRight: "3px solid transparent",
              borderBottom: "4px solid #8F8F8F",
            }}
          />
          <Box
            sx={{
              width: "0",
              height: "0",
              borderLeft: "3px solid transparent",
              borderRight: "3px solid transparent",
              borderTop: "4px solid #8F8F8F",
            }}
          />
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: "76px",
            maxHeight: "180px",
            borderRadius: SPACING_CONSTANTS.borderRadius.sm,
            boxShadow:
              "0 1px 18px 0 rgba(0, 0, 0, 0.12), 0 6px 10px 0 rgba(0, 0, 0, 0.14), 0 3px 5px -1px rgba(0, 0, 0, 0.20)",
          },
        }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      >
        {OPACITY_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            onClick={(e) => {
              e.stopPropagation();
              handleSelect(option.value);
            }}
            sx={{
              minHeight: "24px",
              fontSize: FONT_SIZES.sm,
              fontWeight: 500,
              fontFamily: "Pretendard",
              color:
                option.value === opacity
                  ? COLOR_CONSTANTS.primary.main
                  : COLOR_CONSTANTS.text.primary,
              backgroundColor:
                option.value === opacity
                  ? `${COLOR_CONSTANTS.primary.main}1A`
                  : "transparent",
              borderRadius: SPACING_CONSTANTS.borderRadius.sm,
              margin: `${SPACING_CONSTANTS.spacing[1] / 2}px ${
                SPACING_CONSTANTS.spacing[1]
              }px`,
              "&:hover": {
                backgroundColor:
                  option.value === opacity
                    ? `${COLOR_CONSTANTS.primary.main}1A`
                    : COLOR_CONSTANTS.COLOR.GREY[50],
              },
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

function ColorSelectContainer({
  children,
  onMenuStateChange,
}: ColorSelectContainerProps) {
  const { opacity, changeOpacity } = useColorSelectContext();

  return (
    <Box sx={DROPDOWN_STYLES}>
      <ColorSelectItems>{children}</ColorSelectItems>
      <ColorOpacitySelect
        opacity={opacity}
        onChange={changeOpacity}
        onMenuStateChange={onMenuStateChange}
      />
    </Box>
  );
}

function ColorSelectHeader({
  color,
  opacity,
  onClick,
}: ColorSelectHeaderProps) {
  const colorValue = color ? `${color}, ${opacity * 100}%` : undefined;

  return (
    <Box>
      <TextField
        size="small"
        fullWidth
        placeholder={color ? undefined : "텍스트 컬러 선택"}
        value={colorValue}
        onClick={onClick}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <ColorBox
                  sx={{ width: "18px", height: "18px" }}
                  color={color}
                />
              </InputAdornment>
            ),
            readOnly: true,
            sx: {
              padding: `${SPACING_CONSTANTS.spacing[1] / 2}px ${
                SPACING_CONSTANTS.spacing[3]
              }px 0`,
              height: "32px",
              fontSize: FONT_SIZES.md,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              "& .MuiOutlinedInput-notchedOutline": {
                borderRadius: SPACING_CONSTANTS.borderRadius.sm,
              },
              "& input": {
                padding: "0",
                fontSize: FONT_SIZES.md,
                height: "100%",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                caretColor: "transparent",
              },
            },
          },
        }}
      />
    </Box>
  );
}

function ColorSelectItem({ color, isSelected, onClick }: ColorSelectItemProps) {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick(color);
      }}
    >
      <ColorBox color={color} size="medium" isSelected={isSelected} />
    </div>
  );
}

function ColorSelectItems({ children }: ColorSelectItemsProps) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: `${SPACING_CONSTANTS.spacing[1]}px`,
        flexWrap: "wrap",
      }}
    >
      {children}
    </Box>
  );
}

function ColorSelect({ colors, value, onChange }: ColorSelectProps) {
  const [isShown, open, close, toggle] = useIsShown(false);
  const wrapperRef = useRef<HTMLDivElement>(null!);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useOnClickOutside(wrapperRef as React.RefObject<HTMLElement>, () => {
    if (isShown && !isMenuOpen) close();
  });

  const changeColor = (color: (typeof MARKER_COLORS)[number]) => {
    onChange(color, value.opacity);
  };

  const changeOpacity = (opacity: number) => {
    onChange(value.color, opacity);
  };

  return (
    <ColorSelectContext.Provider
      value={{
        color: value.color,
        opacity: value.opacity,
        changeColor,
        changeOpacity,
      }}
    >
      <Box ref={wrapperRef} sx={{ position: "relative", width: "100%" }}>
        <ColorSelectHeader
          color={value.color}
          opacity={value.opacity}
          onClick={toggle}
        />
        {isShown && (
          <ColorSelectContainer onMenuStateChange={setIsMenuOpen}>
            <ColorSelectItems>
              {colors.map((optionColor) => (
                <ColorSelectItem
                  key={optionColor}
                  color={optionColor}
                  onClick={(selectedColor) => {
                    changeColor(selectedColor);
                    close();
                  }}
                  isSelected={optionColor === value.color}
                />
              ))}
            </ColorSelectItems>
          </ColorSelectContainer>
        )}
      </Box>
    </ColorSelectContext.Provider>
  );
}

export { ColorSelect };
export type { ColorSelectProps, ColorSelectValue };
